import { Modal } from '../Modal.js'
import { Sheet } from '../Sheet.js'
import { useIsNarrow } from '../../lib/media.js'
import { JobCard } from './JobCard.js'

/**
 * A job, opened over whatever the person was looking at.
 *
 * This is the only way a job opens anywhere in the product. It used to be one
 * of two: a popup on the dispatch board, and a full page at `/jobs/:id` that
 * everything else linked to. Two experiences from the same card meant clicking
 * a job from the Jobs list cost the list — its scroll, its page, its filters —
 * and getting back to it was the browser's back button rather than a Close.
 * Now the screen underneath never unmounts and closing is a return. Decision 61.
 *
 * The board's original reasoning turned out to be everyone's: "go to the job"
 * cost the dispatcher the day they were holding in their head, and the answer
 * to "why is this booked here" is in the notes and the history rather than in
 * the booking, so the whole card opens rather than a summary of it.
 *
 * A modal on a desktop, a sheet on a phone. A centred dialog on a 390px screen
 * is only a page with a margin, and it would leave the tab bar showing under a
 * card whose own actions are its bar. Decision 58.
 *
 * The unbooked case is not a second layout. A job off the rail is the same
 * card with an empty visit list, and reading it is the same question: what is
 * this job, before I decide where it goes.
 */
export function JobPopup({ jobId, onClose }: { jobId: string | null; onClose: () => void }) {
  const narrow = useIsNarrow()

  if (narrow) {
    if (jobId === null) return null

    return (
      <Sheet size="tall" scrollBody={false} labelledBy="job-card-title" onClose={onClose}>
        <JobCard jobId={jobId} />
      </Sheet>
    )
  }

  return (
    <Modal
      open={jobId !== null}
      onClose={onClose}
      labelledBy="job-card-title"
      className="h-[min(920px,calc(100vh-2rem))] max-w-[1300px] min-w-0 rounded-[18px]"
    >
      {jobId ? <JobCard jobId={jobId} onClose={onClose} className="h-full" /> : null}
    </Modal>
  )
}
